import { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { Helmet } from "react-helmet-async"
import { arrayMoveImmutable }  from 'array-move'

import type {
    TPossibleScreens,
    TUpload,
    TUploadedFile,
    TUploadingError,
    TErrorText,
} from "../types"

import { baseURL, development, isSnap } from '../constants'

import { statOnLoad } from '../Stat'

import Wrapper from "../elements/Wrapper"
import Content from "../elements/Content"

import {
    Buttons,
    UploadButtons,
    CloudButtons,
    ActionButtons,
    ButtonAction,
    ButtonAddFiles,
    ButtonClearFiles,
    ButtonOneDrive,
    ButtonDropBox,
    ButtonGoogleDrive,
} from "../elements/Buttons"

import { convert, localUpload } from "../Services"

import DropZone from "../screens/DropZone"

const ErrorBlock = loadable(() => import('../elements/Errors'))
const FilesPreview = loadable(() => import('../screens/FilesPreview'))
const Preloader = loadable(() => import("../screens/Preloader"))
const Download = loadable(() => import("../screens/Download"))
const Error = loadable(() => import("../screens/Error"))

const uploadedFilesT: TUploadedFile[] = []
const uploadingErrors: TUploadingError[] = []
let downloadLink: string

const ConvertPDFtoJPG = () => {
    const [currentScreen, switchScreen] = useState<TPossibleScreens>('filespreview')
    const [progress, setProgress] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>(uploadedFilesT)
    const [countUploadingErrors, setCountUploadingErrors] = useState(uploadingErrors.length)
    const [errorText, setErrorText] = useState<TErrorText>({})
    const [showErrorBlock, setShowErrorBlock] = useState(true)

    const [prep, setPrep] = useState('Uploading File')

    const upload: TUpload = {

        action: "pdf_to_jpg",
        allowExtantions: ["pdf"],
        convertTo: "jpg",
        maxSize: 25, // MB
        multiselect: true,

        setDownloadLink: (link: string) => downloadLink = link,
        getDownloadLink: () => downloadLink,
        setProgress: (progress) => setProgress(progress),
        getProgress: () => progress,
        setScreen: (screen) => {
            if (window.scrollY > 220) window.scrollTo({ top: 0, behavior: "smooth" })
            if (screen === 'uploading' || screen === 'converting') setProgress(0)
            return switchScreen(screen)
        },
        getScreen: () => currentScreen,
        addFiles: (uploadedFile) => {
            return setUploadedFiles([...uploadedFiles, ...uploadedFile])
        },
        delFile: (uploadedFile) => {
            return setUploadedFiles(uploadedFiles.filter((element) => element !== uploadedFile))
        },
        clearFiles: () => setUploadedFiles([]),
        getFiles: () => uploadedFiles,
        moveFiles: (oldIndex, newIndex) => {
            return setUploadedFiles(arrayMoveImmutable(uploadedFiles, oldIndex, newIndex))
        },
        addError: (uploadingError) => {
            uploadingErrors.push(uploadingError)
            setShowErrorBlock(true)
            return setCountUploadingErrors(countUploadingErrors + 1)
        },
        delError: (uploadingError) => {
            const idx = uploadingErrors.indexOf(uploadingError)
            if (idx !== -1) {
                uploadingErrors.splice(idx, 1)
                return setCountUploadingErrors(countUploadingErrors - 1)
            }
        },
        clearErrors: () => {
            uploadingErrors.splice(0, uploadingErrors.length)
            return setCountUploadingErrors(0)
        },
        hideErrors: () => setShowErrorBlock(false),
        getErrors: () => uploadingErrors,
        setErrorText: setErrorText,
    }

    useEffect(() => {
        
        const token = location.hash.replace('#', '')

        if (token.includes("id=")) {
            const docid = token.slice(3);
            if (Number.isInteger(Number(docid)) && String(Number(docid)) == docid) {
                localUpload(upload, docid, true)
                setPrep('Prepairing File')
            }
        }
        else if (token.length === 36) {
            localUpload(upload, token)
            setPrep('Prepairing File')
        }
        else statOnLoad(upload)
    },[])


    // useEffect(() => {
    //     const token = location.hash.replace('#', '')
    //     if (token.length === 36) {
    //         location.hash = ''
    //         localUpload(upload, token)
    //     }
    //     else statOnLoad(upload)
    // }, [])

    return (
        <Wrapper>
            <Helmet>
                <title>Convert PDF to JPG | Templateroller</title>
                <meta name="keywords" content="pdf to jpg, convert pdf to jpg, pdf to jpg converter, converting pdf to jpg, convert a pdf to jpg, pdf to jpg free online converter, how to convert pdf to jpg, how to convert pdf to a jpg" />
                <meta name="description" content="Convert PDF to JPG files with TemplateRoller's PDF to JPG free online converter. This web-based application makes conversion easy, quick, and convenient." />
                <meta property='og:title' content="Convert PDF to JPG" />
                <meta property='og:description' content="Use our free online tool to convert PDF to JPG. Extract and save pages from PDF to JPG on your device." />
                <meta property='og:url' content={`${baseURL}/tools/convert-pdf-to-jpg.html`} />
                <meta property="og:image" content={`${baseURL}/tools/assets/og-pdf-to-jpg.png`} />
                <meta property='og:locale' content="en_US" />
                <meta property='twitter:title' content="Convert PDF to JPG" />
                <meta property='twitter:description' content="Use our free online tool to convert PDF to JPG. Extract and save pages from PDF to JPG on your device." />
                <meta property="twitter:image:src" content={`${baseURL}/tools/assets/og-pdf-to-jpg.png`} />
                <link rel='canonical' href={`${baseURL}/tools/convert-pdf-to-jpg.html`} />
            </Helmet>

            {
                currentScreen === 'filespreview' &&
                <h1>Convert PDF&nbsp;to&nbsp;JPG</h1>
            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length === 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <DropZone
                        title="Convert your PDF documents into JPG files with ease and efficiency"
                        description="Convert PDF to JPG files with TemplaterRoller’s PDF to JPG free online converter. This web-based application makes conversion easy, quick, and convenient."
                        buttonText="Browse files"
                        warning={`Upload documents of up to ${upload.maxSize} MB in ${(upload.allowExtantions.length > 2 ? upload.allowExtantions.map((e, i) => i == upload.allowExtantions.length - 1 ? `and .${e}` : `.${e}`) : upload.allowExtantions.map(e => `.${e}`)).join(', ')} format${upload.allowExtantions.length > 1 ? 's' : ''}`}
                        
                        upload={upload}
                    />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                        </UploadButtons>
                    </Buttons>
                    
                </Content>
                
            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length > 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <FilesPreview upload={upload} />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                            <ButtonAddFiles upload={upload} />
                            <ButtonClearFiles upload={upload} />
                        </UploadButtons>
                        <ActionButtons>
                            <ButtonAction
                                onClick={() => {
                                    return convert({
                                        upload: upload,
                                    })
                                }}
                            >Convert to {upload.convertTo.toUpperCase()}</ButtonAction>
                        </ActionButtons>
                    </Buttons>
                </Content>
            }

            {
                currentScreen === 'uploading' &&
                <Content>
                    <Preloader
                        type="progress"
                        title="Uploading Files"
                        description="Don’t close this tab, your files are being uploaded. This shouldn’t take long."
                        progress={progress}
                    />
                </Content>
            }

            {
                currentScreen === 'converting' &&
                <Content>
                    <Preloader
                        type="progress"
                        title="Converting Files"
                        description="Don’t close this tab, your files are being processed. This shouldn’t take long."
                        progress={progress}
                    />
                </Content>
            }

            {
                currentScreen === 'link' &&
                <Content>
                    <Download
                        title="PDF to JPG"
                        description="Your files have been converted to JPG. Click on “Download” to save the file to your device as a ZIP archive."
                        buttonText="Download"

                        upload={upload}
                        onClick={() => {                           
                            // @ts-ignore
                            window.stat?.(`tools_result_${upload.action}`)
                        }}
                    />
                </Content>
            }

            {
                currentScreen === 'error' &&
                <Content>
                    <Error text={errorText} />
                </Content>
            }

        </Wrapper>
    )
}

export default ConvertPDFtoJPG